import { useEffect, useState } from "react";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { Card, Badge, gbp, fmtDate, Loading } from "@/components/ui-bits";
import { TrendingUp, TrendingDown, PoundSterling, Percent } from "lucide-react";
import TeamInbox from "@/components/TeamInbox";

export default function Finance() {
  const [txns, setTxns] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/finance/transactions").then(({ data }) => setTxns(data));
    api.get("/finance/summary").then(({ data }) => setSummary(data));
  }, []);

  if (!txns || !summary) return <Layout title="Finance Centre"><Loading /></Layout>;

  const cards = [
    { label: "Total Income", value: gbp(summary.income), icon: TrendingUp, color: "text-emerald-700 bg-emerald-50" },
    { label: "Total Expenses", value: gbp(summary.expenses), icon: TrendingDown, color: "text-red-700 bg-red-50" },
    { label: "Net Position", value: gbp(summary.net), icon: PoundSterling, color: "text-blue-700 bg-blue-50" },
    { label: "Management Fees", value: gbp(summary.fees), icon: Percent, color: "text-violet-700 bg-violet-50" },
  ];

  return (
    <Layout title="Finance Centre" subtitle="Money">
      <TeamInbox department="Finance" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
        {cards.map((c, i) => (
          <Card key={i} className="p-5 fade-up" data-testid={`finance-card-${i}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${c.color} mb-4`}><c.icon size={20} /></div>
            <p className="text-2xl font-display font-bold tracking-tight text-slate-900 tabular-nums">{c.value}</p>
            <p className="text-sm font-medium text-slate-700 mt-0.5">{c.label}</p>
          </Card>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="font-display text-lg font-semibold text-slate-900">Transactions</h3>
          <p className="text-xs text-slate-400 mt-0.5">Rent collection, expenses, landlord & contractor payments</p>
        </div>
        <table className="w-full">
          <thead className="bg-slate-50 text-slate-500 uppercase text-xs font-semibold tracking-wider">
            <tr>
              <th className="py-3 px-6 text-left">Date</th><th className="text-left">Property</th>
              <th className="text-left">Type</th><th className="text-left">Description</th><th className="text-right px-6">Amount</th>
            </tr>
          </thead>
          <tbody>
            {txns.map((t) => (
              <tr key={t.txn_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid={`txn-${t.txn_id}`}>
                <td className="py-3 px-6 text-sm text-slate-500 font-mono">{fmtDate(t.date)}</td>
                <td className="text-sm text-blue-700">{t.property_address || t.property_ref || "—"}</td>
                <td><Badge>{t.type}</Badge></td>
                <td className="text-sm text-slate-700">{t.description}</td>
                <td className={`text-sm px-6 text-right tabular-nums font-medium ${t.direction === "in" ? "text-emerald-600" : "text-red-500"}`}>
                  {gbp(t.amount)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
